import React from 'react';

// Components
import AddressSelection from './AddressSelection';

// Types
import { Address } from '../../../../../../../types/Common';
import ErrorPage from '../../../../../../common/src/components/ErrorPage';
import { useTranslation } from 'react-i18next';
import { omit } from 'lodash';
import ReuseAddress from './ReuseAddress';
import useSubView from '../../../../hooks/useSubView';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import { useFlowsProgression } from '../../../../hooks/useFlowsProgression';
import {
  isRiskAnalysisFlow,
  useFlowsSelector,
} from '../../../../../../common/src/utils/globalVariables';
import { useFlowsRouteParams } from '../../../../hooks/useFlowsRouteParams';
import { useFlowsSession } from '../../../../api/getFlowsSessionById';
import { flowIdSalesConversation } from '../../../../constants/flowIds';
import useAuth from '../../../../../../app/src/features/authentication/hooks/useAuth';

interface Props {
  question: FlowsQuestion;
}

const AddressInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
}) => {
  const { distribution } = useAuth();
  const { t } = useTranslation();
  const supportedFlowCountries = useFlowsSelector(
    (state) => state.flows.flowsResponse?.info.supported_countries,
  );
  const { sessionId } = useFlowsRouteParams();
  const session = useFlowsSession(sessionId);

  const partyId = session?.data?.prospect?.id || '';
  const supportedCountries = question.rules.address?.allowed_countries;
  const defaultCountry = supportedCountries?.length
    ? supportedCountries?.includes(distribution?.address?.country_code || '')
      ? distribution?.address?.country_code
      : supportedCountries?.[0]
    : distribution?.address?.country_code;
  const {
    field: { value, onBlur },
    prefilled,
    error,
    formState: { errors },
  } = useFlowInputComponent({
    question,
  });
  const { nextQuestion, previousQuestion } = useFlowsProgression();

  const isFirstOfDuplicateGroups =
    question.index === 0 &&
    (session.data?.flow_id === flowIdSalesConversation || isRiskAnalysisFlow());

  const { currentSubView, nextSubView, previousSubView } = useSubView({
    question,
    defaultView: isFirstOfDuplicateGroups ? 'reuseAddress' : 'addressInput',
  });

  const handleAddressChange = (address?: Address | null) => {
    if (!address) return onBlur({ name: question.id, value: null });
    onBlur({
      name: question.id,
      value: {
        ...(value as Address),
        ...omit(address, 'self'),
        _local: { self: address?.self },
      },
    });
  };

  return (
    <>
      {{
        reuseAddress: (
          <ReuseAddress
            onChange={handleAddressChange}
            previousQuestionHandler={previousQuestion}
            nextQuestionHandler={nextQuestion}
            nextSubView={() => nextSubView('addressInput')}
          />
        ),
        addressInput: (
          <AddressSelection
            name={question.id}
            value={value as Address}
            error={error && t('Please fill in a valid address')}
            errors={errors}
            countryCode={
              (value as Address)?.country_code ||
              defaultCountry ||
              supportedFlowCountries?.[0] ||
              ''
            }
            onChange={handleAddressChange}
            previousSubView={
              isFirstOfDuplicateGroups ? previousSubView : previousQuestion
            }
            nextQuestionHandler={nextQuestion}
            prefilled={prefilled}
            supportedCountries={supportedCountries}
            partyId={partyId}
            isLookupAllowed={
              question.rules.address?.autocomplete_enabled !== false
            }
          />
        ),
      }[currentSubView] || <ErrorPage />}
    </>
  );
};
export default AddressInputComponent;
